import type { FormState } from '@vtex/admin-ui-form'
import { useCallback } from 'react'

export type IncompatibilityRule<T> = {
  field: string
  when: T[]
}

export type IncompatibleField = {
  field: string
  value: any
}

export type UseIncompatibleOptions<T> = {
  rules: IncompatibilityRule<T>[]
  onlyFilled?: boolean
  form: FormState
}

export type UseIncompatibleReturn = {
  incompatibleFields: IncompatibleField[]
  hasIncompatibilities: boolean
}

export function useIncompatible<T = any>(options: UseIncompatibleOptions<T>) {
  const { rules, onlyFilled = true, form } = options

  const checkIncompatible = useCallback(
    (watchedValue: T): UseIncompatibleReturn => {
      if (!rules.length) {
        return {
          incompatibleFields: [],
          hasIncompatibilities: false,
        }
      }

      const results: IncompatibleField[] = []

      rules.forEach((rule) => {
        if (!rule.when.includes(watchedValue)) return

        const fieldValue = form.getValues(rule.field)
        const isFilled = !!fieldValue

        if (onlyFilled && !isFilled) return

        results.push({
          field: rule.field,
          value: fieldValue,
        })
      })

      return {
        incompatibleFields: results,
        hasIncompatibilities: results.length > 0,
      }
    },
    [rules, form, onlyFilled]
  )

  return { checkIncompatible }
}
